import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useGlobalSearch, MIN_QUERY_LENGTH } from '../../hooks/useGlobalSearch';
import { SearchResults, SearchResultItem, flattenResults } from './SearchResults';
import { rfqPath } from '../../utils/wizardRoutes';

// Every hit lands on its RFQ: bare /rfqs/:rfqId resolves to the RFQ's current
// step (dedicated supplier/datasheet pages come with their own sections).
// Single place to update when the RFQ route scheme changes.
const rfqRoute = (rfqId: string) => rfqPath(rfqId);

/**
 * The top-bar global search: debounced org-scoped typeahead with grouped
 * results (RFQs / Suppliers / Parts), arrow-key navigation, Enter to open,
 * Escape to close. Recent searches show before the query is long enough.
 */
export const GlobalSearch: React.FC<{ placeholder?: string; autoFocus?: boolean }> = ({
  placeholder = 'Search RFQs, suppliers, parts…',
  autoFocus = false,
}) => {
  const navigate = useNavigate();
  const { query, setQuery, trimmed, data, loading, error, recents, rememberQuery } = useGlobalSearch();
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const showRecents = trimmed.length < MIN_QUERY_LENGTH;
  const items = useMemo(() => flattenResults(data, recents, showRecents), [data, recents, showRecents]);
  const hasContent = showRecents ? items.length > 0 : true; // results mode always shows a state row
  const open = focused && !dismissed && hasContent;

  useEffect(() => setActiveIndex(0), [items]);

  // The phone's top bar swaps itself out for this field; whoever tapped the
  // magnifier has already decided to type, so open the keyboard with it.
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Close on any click outside the input + panel.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setFocused(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const select = (item: SearchResultItem) => {
    if (item.kind === 'recent') {
      setQuery(item.value);
      inputRef.current?.focus();
      return;
    }
    rememberQuery(trimmed);
    setFocused(false);
    inputRef.current?.blur();
    navigate(rfqRoute(item.hit.rfqId));
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setDismissed(true);
      inputRef.current?.blur();
      return;
    }
    if (!open || items.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + items.length) % items.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (items[activeIndex]) select(items[activeIndex]);
    }
  };

  return (
    <div ref={rootRef} className="relative min-w-0 flex-1 md:w-[clamp(160px,22vw,260px)] md:flex-none">
      <div className="flex items-center gap-s3 rounded-control border border-line-strong bg-surface px-[12px] py-[7px]">
        <Search className="h-[15px] w-[15px] shrink-0 text-ink-muted" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setDismissed(false);
          }}
          onFocus={() => {
            setFocused(true);
            setDismissed(false);
          }}
          onBlur={(e) => {
            // Result clicks keep focus (mousedown is prevented); this only
            // fires when focus truly leaves, e.g. tabbing to the bell.
            if (!rootRef.current?.contains(e.relatedTarget as Node)) setFocused(false);
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label="Search"
          role="combobox"
          aria-expanded={open}
          aria-controls="global-search-results"
          aria-activedescendant={open && items.length > 0 ? `global-search-option-${activeIndex}` : undefined}
          className="w-full border-none bg-transparent font-sans text-[16px] text-ink outline-none placeholder:text-ink-muted md:text-body-md"
        />
      </div>
      {open && (
        <SearchResults
          query={trimmed}
          data={data}
          loading={loading}
          error={error}
          items={items}
          showRecents={showRecents}
          activeIndex={activeIndex}
          onSelect={select}
          onHover={setActiveIndex}
        />
      )}
    </div>
  );
};

export default GlobalSearch;
